/**
 * Design-system gallery — the in-app NORTH STAR reference for the FSE
 * ("Mango — Farmer Market") design language. Renders the full @fse/ui kit on
 * the live MUI theme + self-hosted brand fonts, so the look can be reviewed in
 * one place before it is rolled across product screens.
 *
 * Public, unauthenticated route (`/design`). NOT linked from the product nav —
 * it is a reference surface, not a feature. Built from @fse/ui exclusively.
 */
import { useState, type ReactNode } from 'react';
import {
  Badge,
  BottomNav,
  Box,
  Button,
  Card,
  CardContent,
  ColorModeToggle,
  Container,
  Fab,
  Icon,
  LanguagePicker,
  ListItem,
  Menu,
  Money,
  OtpInput,
  PhoneInput,
  Stepper,
  SearchBar,
  SegmentedButton,
  Stack,
  StatCard,
  StatGrid,
  StatusPill,
  Switch,
  Chip,
  Text,
  TextField,
  TopAppBar,
  brand,
} from '@fse/ui';

const LANGS = [
  { code: 'en', native: 'English', english: 'English' },
  { code: 'zu', native: 'isiZulu', english: 'Zulu' },
  { code: 'xh', native: 'isiXhosa', english: 'Xhosa' },
  { code: 'st', native: 'Sesotho', english: 'Sotho' },
  { code: 'af', native: 'Afrikaans', english: 'Afrikaans' },
];

const NAV_ITEMS = [
  { value: 'home', label: 'Home', icon: 'home' },
  { value: 'market', label: 'Market', icon: 'storefront' },
  { value: 'batches', label: 'Batches', icon: 'inventory_2' },
  { value: 'me', label: 'Me', icon: 'person' },
];

const SEG_OPTIONS = [
  { value: 'sell', label: 'Sell', icon: 'sell' },
  { value: 'donate', label: 'Donate', icon: 'volunteer_activism' },
  { value: 'store', label: 'Store', icon: 'inventory_2' },
];

const SCALES: { name: string; ramp: Record<string | number, string> }[] = [
  { name: 'mango', ramp: brand.mango },
  { name: 'leaf', ramp: brand.leaf },
  { name: 'ripe', ramp: brand.ripe },
  { name: 'earth', ramp: brand.earth },
  { name: 'paper', ramp: brand.paper },
  { name: 'alert', ramp: brand.alert },
];

const TYPE_VARIANTS = [
  'display-large',
  'display-small',
  'headline-medium',
  'title-large',
  'title-medium',
  'body-large',
  'body-medium',
  'label-large',
  'label-small',
] as const;

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Box component="section" sx={{ scrollMarginTop: 16 }}>
      <Text variant="headline-small" as="h2">
        {title}
      </Text>
      {subtitle ? (
        <Box sx={{ mt: 0.5, mb: 2 }}>
          <Text variant="body-medium" color="text.secondary">
            {subtitle}
          </Text>
        </Box>
      ) : (
        <Box sx={{ mb: 2 }} />
      )}
      <Card>
        <CardContent>{children}</CardContent>
      </Card>
    </Box>
  );
}

function Wrap({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>{children}</Box>;
}

export function DesignGalleryScreen() {
  const [otp, setOtp] = useState('41');
  const [pin, setPin] = useState('1234');
  const [phone, setPhone] = useState('821234567');
  const [lang, setLang] = useState('zu');
  const [seg, setSeg] = useState('sell');
  const [segMulti, setSegMulti] = useState<string[]>(['sell', 'store']);
  const [notify, setNotify] = useState(true);
  const [search, setSearch] = useState('');
  const [nav, setNav] = useState('market');
  const [step, setStep] = useState(1);
  const [filter, setFilter] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
      <TopAppBar title="FSE Design System" centerTitle actions={<ColorModeToggle />} />

      <Container maxWidth="md" sx={{ pt: 4 }}>
        <Box sx={{ mb: 1 }}>
          <Text variant="display-small" as="h1">
            Mango — Farmer Market
          </Text>
        </Box>
        <Text variant="body-large" color="text.secondary">
          North-star reference for the FSE design language — Material Design 3, rural-bumped, on
          self-hosted Bricolage Grotesque + Noto Sans. Every component below is from{' '}
          <code>@fse/ui</code>.
        </Text>

        <Stack spacing={5} sx={{ mt: 4 }}>
          {/* ---- Colour ---- */}
          <Section title="Colour" subtitle="Brand scales → MD3 roles → semantic aliases">
            <Stack spacing={2}>
              {SCALES.map((scale) => (
                <Box key={scale.name}>
                  <Box sx={{ mb: 0.5 }}>
                    <Text variant="label-medium" color="text.secondary">
                      {scale.name}
                    </Text>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(scale.ramp).map(([shade, hex]) => (
                      <Box key={shade} sx={{ width: 84 }}>
                        <Box
                          sx={{
                            height: 48,
                            borderRadius: 1.5,
                            bgcolor: hex,
                            border: '1px solid', borderColor: 'divider',
                          }}
                        />
                        <Box sx={{ mt: 0.5 }}>
                          <Text variant="label-small" color="text.secondary">
                            {shade} · {hex}
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Section>

          {/* ---- Typography ---- */}
          <Section title="Typography" subtitle="Display = Bricolage Grotesque · Body/label = Noto Sans">
            <Stack spacing={1.5}>
              {TYPE_VARIANTS.map((v) => (
                <Box key={v} sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ width: 130, flexShrink: 0 }}>
                    <Text variant="label-small" color="text.secondary">
                      {v}
                    </Text>
                  </Box>
                  <Text variant={v}>Fresh mangoes, fairly priced</Text>
                </Box>
              ))}
            </Stack>
          </Section>

          {/* ---- Buttons ---- */}
          <Section title="Buttons" subtitle="Pill shape · rural touch targets (44 / 48 / 64dp)">
            <Stack spacing={2}>
              <Wrap>
                <Button variant="contained">Filled</Button>
                <Button variant="outlined">Outlined</Button>
                <Button variant="text">Text</Button>
                <Button variant="contained" color="secondary">
                  Secondary
                </Button>
                <Button variant="contained" disabled>
                  Disabled
                </Button>
              </Wrap>
              <Wrap>
                <Button variant="contained" size="small">
                  Small
                </Button>
                <Button variant="contained" size="medium">
                  Medium
                </Button>
                <Button variant="contained" size="large">
                  Large CTA
                </Button>
              </Wrap>
              <Wrap>
                <Fab icon="add" onClick={() => {}} />
                <Fab icon="add" label="Capture harvest" onClick={() => {}} />
                <Fab icon="edit" size="lg" color="tertiary" onClick={() => {}} />
              </Wrap>
            </Stack>
          </Section>

          {/* ---- Inputs ---- */}
          <Section title="Inputs" subtitle="PIN / OTP, phone, search, segmented, switch">
            <Stack spacing={3}>
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-medium" color="text.secondary">
                    OTP — 5 digits, voice-friendly
                  </Text>
                </Box>
                <OtpInput value={otp} onChange={setOtp} length={5} aria-label="One-time code" />
              </Box>
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-medium" color="text.secondary">
                    PIN — masked
                  </Text>
                </Box>
                <OtpInput value={pin} onChange={setPin} length={4} mask aria-label="PIN" />
              </Box>
              <PhoneInput value={phone} onChangeText={setPhone} hint="We'll send a one-time code" />
              <TextField label="Farm name" placeholder="e.g. Khumalo Smallholding" />
              <SearchBar value={search} onChange={setSearch} placeholder="Search listings" />
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-medium" color="text.secondary">
                    Segmented — single select
                  </Text>
                </Box>
                <SegmentedButton
                  options={SEG_OPTIONS}
                  value={seg}
                  onChange={(v) => typeof v === 'string' && setSeg(v)}
                  aria-label="Intent"
                />
              </Box>
              <Box>
                <Box sx={{ mb: 1 }}>
                  <Text variant="label-medium" color="text.secondary">
                    Segmented — multi select
                  </Text>
                </Box>
                <SegmentedButton
                  options={SEG_OPTIONS}
                  value={segMulti}
                  multiple
                  onChange={(v) => Array.isArray(v) && setSegMulti(v)}
                  aria-label="Channels"
                />
              </Box>
              <Switch checked={notify} onChange={setNotify} label="Notify me about new offers" />
            </Stack>
          </Section>

          {/* ---- Language selector ---- */}
          <Section title="Language selector" subtitle="Native name + English subtitle · 64dp rows">
            <LanguagePicker options={LANGS} value={lang} onChange={setLang} aria-label="Choose your language" />
          </Section>

          {/* ---- Chips, badges, status ---- */}
          <Section title="Chips · Badges · Status" subtitle="Filter / assist chips, count badges, status pills">
            <Stack spacing={2}>
              <Wrap>
                <Chip label="All" kind="filter" selected={filter} onClick={() => setFilter((f) => !f)} />
                <Chip label="Verified" kind="assist" color="success" leadingIcon="verified" />
                <Chip label="Pending" kind="assist" color="warning" />
                <Chip label="Organic" kind="filter" leadingIcon="eco" />
                <Chip label="Dismissable" kind="input" color="neutral" onDelete={() => {}} />
              </Wrap>
              <Wrap>
                <Badge count={3}>
                  <Icon name="notifications" size={28} aria-label="Notifications" />
                </Badge>
                <Badge count={128} max={99} color="success">
                  <Icon name="mail" size={28} aria-label="Messages" />
                </Badge>
                <Badge dot color="tertiary">
                  <Icon name="storefront" size={28} aria-label="Market" />
                </Badge>
              </Wrap>
              <Wrap>
                <StatusPill variant="active" icon="check_circle">
                  Active
                </StatusPill>
                <StatusPill variant="due" icon="schedule">
                  Due soon
                </StatusPill>
                <StatusPill variant="overdue" icon="error">
                  Overdue
                </StatusPill>
                <StatusPill variant="upcoming">Upcoming</StatusPill>
                <StatusPill variant="sold">Sold</StatusPill>
              </Wrap>
            </Stack>
          </Section>

          {/* ---- Data display ---- */}
          <Section title="Data display" subtitle="Money, stat grid, list items">
            <Stack spacing={3}>
              <Wrap>
                <Money amount={12500} currency="R" size="display" />
                <Money amount={45} unit="/ kg" size="lg" color="primary.main" />
                <Money amount={60} unit="/ kg" size="md" strike color="text.secondary" />
              </Wrap>
              <StatGrid columns={2}>
                <StatCard label="Seasons sold" value="14" color="leaf" icon="agriculture" delta="+3 vs last year" deltaDirection="up" />
                <StatCard label="Total earned" value="R 92,500" color="mango" icon="payments" />
                <StatCard label="Active listings" value="6" color="blue" icon="storefront" />
                <StatCard label="Avg. quality" value="92%" color="purple" icon="star" delta="-2%" deltaDirection="down" />
              </StatGrid>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <ListItem
                  leadingIcon="person"
                  title="Nomsa Khumalo"
                  supportingText="Khumalo Smallholding · Limpopo"
                  trailing={<StatusPill variant="active">Verified</StatusPill>}
                  onClick={() => {}}
                />
                <ListItem
                  leadingIcon="inventory_2"
                  title="Mango batch #A-19"
                  supportingText="240 kg · graded A"
                  trailing={<Money amount={10800} size="sm" />}
                  onClick={() => {}}
                />
                <ListItem
                  leadingIcon="notifications"
                  title="Push notifications"
                  trailing={<Switch checked={notify} onChange={setNotify} aria-label="Push notifications" />}
                  noDivider
                />
              </Box>
            </Stack>
          </Section>

          {/* ---- Navigation ---- */}
          <Section title="Navigation" subtitle="Progress stepper, menu, bottom nav">
            <Stack spacing={3}>
              <Stepper steps={['Phone', 'Verify', 'Profile']} activeStep={step} />
              <Wrap>
                <Button variant="outlined" size="small" onClick={() => setStep((s) => Math.max(0, s - 1))}>
                  Back
                </Button>
                <Button variant="contained" size="small" onClick={() => setStep((s) => Math.min(2, s + 1))}>
                  Next
                </Button>
              </Wrap>
              <Box>
                <Button variant="outlined" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                  Open menu
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  items={[
                    { label: 'Edit', icon: 'edit', onClick: () => {} },
                    { label: 'Share', icon: 'share', onClick: () => {} },
                    { label: 'Delete', icon: 'delete', onClick: () => {}, destructive: true },
                  ]}
                />
              </Box>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
                <BottomNav items={NAV_ITEMS} value={nav} onChange={setNav} />
              </Box>
            </Stack>
          </Section>

          {/* ---- Iconography ---- */}
          <Section title="Iconography" subtitle="Material Symbols Rounded — outlined + filled, weight axis">
            <Wrap>
              {['storefront', 'agriculture', 'payments', 'eco', 'person', 'verified', 'language', 'notifications'].map(
                (n) => (
                  <Box key={n} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72, gap: 0.5 }}>
                    <Icon name={n} size={32} aria-label={n} />
                    <Text variant="label-small" color="text.secondary">
                      {n}
                    </Text>
                  </Box>
                ),
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 72, gap: 0.5 }}>
                <Icon name="favorite" size={32} fill color={brand.alert[600]} aria-label="filled" />
                <Text variant="label-small" color="text.secondary">
                  filled
                </Text>
              </Box>
            </Wrap>
          </Section>
        </Stack>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Text variant="label-small" color="text.secondary">
            @fse/ui · Mango — Farmer Market design language · not yet wired into product screens
          </Text>
        </Box>
      </Container>
    </Box>
  );
}
